import { getZodiacSign, validateBirthDate } from './zodiac-rules.js';
import { MATERIALS, parseMaterialRules, selectMaterialRecommendations, sortRecommendationsByPrice } from './crystal-materials.js';
import { createCrystalCatalogLoader } from './crystal-catalog.js';

// Reuse the catalog across signs, keyed by locale, market and preview theme.
// Each page has its own timeout so a large result set is never cut off by a
// timer for the entire catalog. Birthdays never enter the URL or the cache.
const loadCatalog = createCrystalCatalogLoader({ fetchPage: fetchCrystalPage });

async function fetchCrystalPage(url, signal) {
  const request = new AbortController();
  const abort = () => request.abort(signal.reason);
  if (signal.aborted) abort();
  else signal.addEventListener('abort', abort, { once: true });
  const timeout = setTimeout(() => request.abort(), 20000);
  try {
    const response = await fetch(url, { signal: request.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Crystal catalog failed');
    const document = new DOMParser().parseFromString(await response.text(), 'text/html');
    const catalog = document.querySelector('[data-crystal-catalog]');
    if (!catalog) throw new Error('Crystal catalog returned invalid markup');
    const products = [];
    for (const template of catalog.querySelectorAll('template[data-crystal-product]')) {
      const { productId: id, productTitle: title, productPrice } = template.dataset;
      if (id && title) products.push({ id, title, price: productPrice ? Number(productPrice) : NaN, available: true, template });
    }
    return {
      products,
      currentPage: Number(catalog.dataset.currentPage),
      totalPages: Number(catalog.dataset.totalPages),
    };
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', abort);
  }
}

class BirthdayQuiz extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.form = this.querySelector('[data-birthday-form]');
    this.result = this.querySelector('[data-birthday-result]');
    this.error = this.querySelector('[data-birthday-error]');
    this.fields = ['year', 'month', 'day'].map((part) => this.querySelector(`[data-birthday-${part}]`));
    if (!this.form || !this.result || !this.error || this.fields.some((field) => !field)) return;

    this.clearResult();
    this.fields[0].max = String(new Date().getFullYear());
    this.form.hidden = false;
    this.querySelector('[data-birthday-fallback]')?.setAttribute('hidden', '');
    this.form.addEventListener('submit', (event) => this.submit(event), options);
    this.form.addEventListener('input', () => this.clearResult(), options);
    this.form.addEventListener('change', () => this.clearResult(), options);
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-crystal-prev]')) {
        this.scrollProducts(-1);
        return;
      }
      if (event.target.closest('[data-crystal-next]')) {
        this.scrollProducts(1);
        return;
      }
      if (event.target.closest('[data-crystal-retry]')) {
        if (this.activeTemplate) this.showResult(this.activeTemplate, false);
        return;
      }
      if (!event.target.closest('[data-birthday-reset]')) return;
      this.form.reset();
      this.clearResult();
      this.fields[0].focus();
    }, options);

    // Section replacement/reordering is safe: listeners are removed on disconnect.
    // Selecting a match block in the editor previews that result without a birthday.
    document.addEventListener('shopify:block:select', (event) => {
      const template = Array.from(this.querySelectorAll('template[data-zodiac]'))
        .find((item) => item.dataset.blockId === event.detail?.blockId);
      if (template) this.showResult(template, false);
    }, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.requestController?.abort();
    this.requestController = null;
    this.disposeCarousel();
  }

  clearResult() {
    this.requestController?.abort();
    this.requestController = null;
    this.activeTemplate = null;
    this.disposeCarousel();
    this.result.removeAttribute('aria-busy');
    this.error.textContent = '';
    this.fields.forEach((field) => field.removeAttribute('aria-invalid'));
    this.result.hidden = true;
    this.result.replaceChildren();
  }

  submit(event) {
    event.preventDefault();
    this.clearResult();
    const [year, month, day] = this.fields.map((field) => field.value.trim());
    const error = validateBirthDate(year, month, day);
    if (error) {
      this.error.textContent = this.dataset[`${error}Message`];
      this.fields.forEach((field) => field.setAttribute('aria-invalid', 'true'));
      (this.fields.find((field) => !field.value) || (error === 'future' ? this.fields[0] : this.fields[2])).focus();
      return;
    }
    const sign = getZodiacSign(month, day);
    const template = this.querySelector(`template[data-zodiac="${sign}"]`);
    if (template) this.showResult(template, true);
  }

  async showResult(template, focus) {
    this.requestController?.abort();
    this.disposeCarousel();
    this.result.removeAttribute('aria-busy');
    this.activeTemplate = template;
    const request = new AbortController();
    this.requestController = request;
    const materialIds = parseMaterialRules(template.dataset.materials || '');
    this.result.replaceChildren(template.content.cloneNode(true));
    this.result.hidden = false;
    if (focus) this.result.querySelector('[data-result-heading]')?.focus({ preventScroll: true });
    this.result.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'nearest',
    });

    const loading = this.result.querySelector('[data-crystal-loading]');
    const productsList = this.result.querySelector('[data-crystal-products]');
    const carousel = this.result.querySelector('[data-crystal-carousel]');
    const count = this.result.querySelector('[data-crystal-count]');
    const empty = this.result.querySelector('[data-crystal-empty]');
    const error = this.result.querySelector('[data-crystal-error]');
    if (!materialIds.length) {
      loading.hidden = true;
      empty.hidden = false;
      return;
    }

    this.result.setAttribute('aria-busy', 'true');
    const cards = new Map();
    const recommendations = [];
    this.setupCarousel(productsList, request.signal);
    try {
      await loadCatalog(this.catalogUrl(), {
        signal: request.signal,
        onPage: ({ products, page, totalPages }) => {
          if (request.signal.aborted || this.requestController !== request) return;
          for (const { product, materialId } of selectMaterialRecommendations(products, materialIds)) {
            if (cards.has(product.id)) continue;
            const card = product.template.content.cloneNode(true);
            card.querySelector('[data-crystal-match]').textContent = MATERIALS[materialId].label;
            cards.set(product.id, card.firstElementChild);
            recommendations.push({ product, materialId });
          }
          // Sort all loaded pages by the price shown on the card. Shopify's
          // collection sort can use a different variant price; reuse existing
          // card nodes when a later page needs to be inserted earlier.
          sortRecommendationsByPrice(recommendations).forEach(({ product }, index) => {
            const card = cards.get(product.id);
            if (productsList.children[index] !== card) productsList.insertBefore(card, productsList.children[index] || null);
          });
          carousel.hidden = cards.size === 0;
          count.textContent = this.dataset.countMessage.replace('[count]', String(cards.size));
          if (page < totalPages && cards.size) loading.textContent = this.dataset.loadingMoreMessage;
          this.queueCarouselUpdate();
        },
      });
      if (request.signal.aborted || this.requestController !== request) return;
      empty.hidden = cards.size > 0;
    } catch {
      if (this.requestController === request && !request.signal.aborted) {
        if (cards.size) error.querySelector('[data-crystal-error-message]').textContent = this.dataset.partialErrorMessage;
        error.hidden = false;
      }
    } finally {
      if (this.requestController === request) {
        loading.hidden = true;
        this.result.removeAttribute('aria-busy');
      }
    }
  }

  catalogUrl() {
    // Fetch the complete catalog once per market; no birthdays or signs are sent.
    const url = new URL(this.dataset.catalogUrl, window.location.origin);
    url.searchParams.set('section_id', 'birthday-crystal-results');
    url.searchParams.set('sort_by', 'price-ascending');
    const currentUrl = new URL(window.location.href);
    const country = currentUrl.searchParams.get('country') || window.Shopify?.country;
    if (/^[A-Z]{2}$/.test(country || '')) url.searchParams.set('country', country);
    const previewTheme = currentUrl.searchParams.get('preview_theme_id');
    if (previewTheme && /^\d+$/.test(previewTheme)) url.searchParams.set('preview_theme_id', previewTheme);

    return url;
  }

  setupCarousel(track, signal) {
    this.track = track;
    this.previous = this.result.querySelector('[data-crystal-prev]');
    this.next = this.result.querySelector('[data-crystal-next]');
    track.addEventListener('scroll', () => this.queueCarouselUpdate(), { passive: true, signal });
    track.addEventListener('keydown', (event) => {
      if (event.target !== track) return;
      const rtl = getComputedStyle(track).direction === 'rtl';
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.scrollProducts((event.key === 'ArrowRight') !== rtl ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        track.scrollTo({ left: event.key === 'Home' ? 0 : (rtl ? -1 : 1) * track.scrollWidth, behavior: 'instant' });
      }
    }, { signal });
    this.resizeObserver = new ResizeObserver(() => this.queueCarouselUpdate());
    this.resizeObserver.observe(track);
  }

  scrollProducts(direction) {
    if (!this.track) return;
    const style = getComputedStyle(this.track);
    const distance = this.track.clientWidth + (parseFloat(style.columnGap) || 0);
    this.track.scrollBy({
      left: direction * (style.direction === 'rtl' ? -1 : 1) * distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
    });
  }

  queueCarouselUpdate() {
    if (this.carouselFrame || !this.track) return;
    this.carouselFrame = requestAnimationFrame(() => {
      this.carouselFrame = null;
      const maximum = this.track.scrollWidth - this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      this.previous.disabled = position < 2;
      this.next.disabled = maximum - position < 2;
    });
  }

  disposeCarousel() {
    this.resizeObserver?.disconnect();
    if (this.carouselFrame) cancelAnimationFrame(this.carouselFrame);
    this.carouselFrame = null;
    this.track = null;
  }
}

if (!customElements.get('birthday-quiz')) customElements.define('birthday-quiz', BirthdayQuiz);
